"use client";

import { useEffect } from "react";
import type { ProductInformation } from "../PersistentCartPaymentOptions/PersistentCartPaymentOptions";
import { currencyFormatter } from "../UtilsHelper";

const notUndefined = (value: unknown): value is NonNullable<unknown> =>
  typeof value !== "undefined";
const currencyBasedLocale: { [key: string]: string } = {
  USD: "en-US",
  GBP: "en-GB",
};
const paymentOptions = { ANNUAL: "annual", MONTHLY: "monthly" };

export const productInfoTexts = (
  productsInfo: ProductInformation[],
  key: keyof ProductInformation,
): string[] => {
  return productsInfo
    .map(productInfo => productInfo[key])
    .filter(notUndefined)
    .filter((value): value is string => typeof value === "string");
};

export const upfrontPaymentAmount = (
  productInfo: ProductInformation,
): number => {
  if (productInfo.selectedPaymentOption === paymentOptions.ANNUAL) {
    return productInfo.availablePaymentOptions.annual.amount;
  }
  return productInfo.availablePaymentOptions.monthly.downpaymentAmount;
};

export const monthlyProductsInfo = (
  productsInfo: ProductInformation[],
): ProductInformation[] => {
  return productsInfo.reduce((accumulator, productInfo) => {
    if (productInfo.selectedPaymentOption === paymentOptions.MONTHLY) {
      return accumulator.concat(productInfo);
    }
    return accumulator;
  }, [] as ProductInformation[]);
};

export const upfrontPayments = (
  productsInfo: ProductInformation[],
  currency: string,
): { labels: string[]; value: string }[] => {
  return productsInfo.map(productInfo => ({
    labels: productInfo.coverNames,
    value: currencyFormatter(currency).format(
      upfrontPaymentAmount(productInfo),
    ),
  }));
};

export const upfrontPaymentsTotal = (
  productsInfo: ProductInformation[],
  currency: string,
): string => {
  const initialValue = 0;
  const total = productsInfo.reduce((accumulator, productInfo) => {
    return accumulator + upfrontPaymentAmount(productInfo);
  }, initialValue);
  return currencyFormatter(currency).format(total);
};

export const monthlyPayments = (
  productsInfo: ProductInformation[],
  currency: string,
  hasMonthlyFees: boolean = false,
): { labels: string[]; value: string }[] => {
  const monthlyProductsData = monthlyProductsInfo(productsInfo);

  if (!monthlyProductsData.length) return [];

  const flattenInstallmentsSchedule = monthlyProductsData.flatMap(
    (productInfo: ProductInformation) => {
      return productInfo.availablePaymentOptions.monthly.installmentsSchedule;
    },
  );

  const installmentsSchedule = Object.values(
    flattenInstallmentsSchedule.reduce(
      (acc, { month, year, amount }) => {
        const formattedMonth = `${year}-${month}`;
        acc[formattedMonth] = {
          month: formattedMonth,
          amount:
            (acc[formattedMonth] ? acc[formattedMonth].amount : 0) + amount,
        };
        return acc;
      },
      {} as Record<string, { month: string; amount: number }>,
    ),
  ).sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());

  const groupedAmounts = installmentsSchedule.reduce(
    (acc, schedule) => {
      if (acc.last && acc.last !== schedule.amount) {
        acc.result.push([]);
      }
      acc.result[acc.result.length - 1].push(schedule.amount);
      return { ...acc, last: schedule.amount };
    },
    { result: [[]] as number[][], last: null as number | null },
  ).result;

  return groupedAmounts.map((group, index) => {
    const prefix = index === 0 ? "" : "Then ";
    const labels = hasMonthlyFees
      ? productsInfo[0].coverNames
      : [`${prefix}${group.length} monthly payments of`];
    return {
      labels,
      value: currencyFormatter(currency).format(group[0]),
    };
  });
};

export const firstMonthlyPaymentDate = (
  productsInfo: ProductInformation[],
  currency: string,
): string => {
  const monthlyPaymentDates: Date[] = monthlyProductsInfo(productsInfo).map(
    (productInfo: ProductInformation) => {
      return new Date(
        productInfo.availablePaymentOptions.monthly.firstInstallmentDate,
      );
    },
  );

  return new Date(
    Math.min(...monthlyPaymentDates.map(date => date.getTime())),
  ).toLocaleDateString(currencyBasedLocale[currency.toUpperCase()], {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

export const monthlyPaymentSummary = (
  productsInfo: ProductInformation[],
  currency: string,
): { label: string; value: string }[] => {
  const monthlyProducts = monthlyProductsInfo(productsInfo);
  const payments = monthlyProducts.map(productInfo => ({
    installmentCount:
      productInfo.availablePaymentOptions?.monthly?.installmentsSchedule
        ?.length ?? 0,
    monthlyAmount: Number(
      productInfo.availablePaymentOptions?.monthly?.installmentAmount ?? 0,
    ),
  }));

  if (!payments.length) return [];

  const uniqueInstallmentCounts = [
    ...new Set(payments.map(payment => payment.installmentCount)),
  ].sort((a, b) => a - b);
  const allProductsHaveSameInstallmentCount =
    uniqueInstallmentCounts.length === 1;

  const createPaymentTier = (
    paymentCount: number,
    totalAmount: number,
    isFirstTier: boolean,
  ) => {
    const pluralSuffix = paymentCount > 1 ? "s" : "";
    const prefix = isFirstTier ? "" : "Then ";
    return {
      label: `${prefix}${paymentCount} monthly payment${pluralSuffix} of`,
      value: currencyFormatter(currency).format(totalAmount),
    };
  };

  if (allProductsHaveSameInstallmentCount) {
    const installmentCount = uniqueInstallmentCounts[0];
    const totalMonthlyAmount = payments.reduce(
      (sum, payment) => sum + payment.monthlyAmount,
      0,
    );
    return [createPaymentTier(installmentCount, totalMonthlyAmount, true)];
  } else {
    const tiers: { label: string; value: string }[] = [];
    let previousInstallmentCount = 0;

    for (const currentInstallmentCount of uniqueInstallmentCounts) {
      const installmentCountForTier =
        currentInstallmentCount - previousInstallmentCount;
      const monthlyPaymentAmountForTier = payments.reduce((sum, payment) => {
        return payment.installmentCount >= currentInstallmentCount
          ? sum + payment.monthlyAmount
          : sum;
      }, 0);
      const isFirstTier = previousInstallmentCount === 0;

      tiers.push(
        createPaymentTier(
          installmentCountForTier,
          monthlyPaymentAmountForTier,
          isFirstTier,
        ),
      );
      previousInstallmentCount = currentInstallmentCount;
    }

    return tiers;
  }
};

export const selectProductsMarkedForPurchase = (
  products: ProductInformation[],
): ProductInformation[] =>
  products.filter(product => product.markedForPurchase);

export const useTriggerEventTracker = (
  eventName: string | null,
  ...args: string[]
) => {
  useEffect(() => {
    if (eventName && window) {
      window.EventTracker?.trigger(eventName, ...args);
    }
  }, [eventName, args]);
};
